import { notify } from "@/components/common/Notification";
import {
  clearLocalStorage,
  getAccessToken,
  getRefreshToken,
  getTenantId,
  setLocalAccessToken,
  setLocalRefreshToken,
} from "@/lib/storeManager";
import { hidenMessageAPI } from "@/utils/constant/hidenMessageAPI";
import { isPropertyPortalPathname } from "@/project/propertyPortal/utils/isPropertyPortalPathname";
import axios from "axios";

const apiBaseURL = import.meta.env.VITE_API_BASE_URL;

if (!apiBaseURL) {
  console.error(
    "VITE_API_BASE_URL is not defined. Please set it in your environment variables."
  );
}

export const axiosConfig = axios.create({
  baseURL: apiBaseURL ?? "",
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

axiosConfig.interceptors.request.use(config => {
  const token = getAccessToken();
  const tenantId = getTenantId();

  // Don't add Authorization header for refresh token requests
  // For property portal, still use token if available (for local dev), but don't block if missing
  if (
    token &&
    config.headers &&
    !config.url?.includes("/auth/token-refresh/")
  ) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Always add tenant ID if available (backend requires it)
  if (tenantId && config.headers) {
    config.headers["X-Tenant-Id"] = tenantId;
  }

  return config;
});

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

let lastNetworkErrorToastAt = 0;
const NETWORK_ERROR_DEBOUNCE_MS = 4000;

function notifyNetworkErrorDebounced() {
  const now = Date.now();
  if (now - lastNetworkErrorToastAt < NETWORK_ERROR_DEBOUNCE_MS) {
    return;
  }
  lastNetworkErrorToastAt = now;
  notify.error(
    "Network Error",
    "Unable to connect to the server. Please check your internet connection."
  );
}

axiosConfig.interceptors.response.use(
  response => {
    const apiStatus = response.status;
    if (
      hidenMessageAPI.some(path => (response.config.url ?? "").includes(path))
    ) {
      return response;
    }
    if (
      (apiStatus === 200 || apiStatus === 201 || apiStatus === 204) &&
      response.data?.success
    ) {
      const message = response.data?.success?.message ?? response.data.message;
      if (message) {
        notify.success(message);
      }
    }
    return response;
  },
  async error => {
    const originalRequest = error.config;
    const isPropertyPortal = isPropertyPortalPathname(window.location.pathname);

    // Don't silently fail login/logout endpoints - they need to show errors
    const url = originalRequest?.url ?? "";
    const isAuthEndpoint = [
      "/auth/login/",
      "/auth/logout/",
      "/auth/token-refresh/",
    ].some(endpoint => url.includes(endpoint));

    // No HTTP response: offline, DNS, CORS, SSL, or server unreachable — one debounced toast
    if (!error.response) {
      if (!isAuthEndpoint) {
        notifyNetworkErrorDebounced();
      } else {
        notify.error(
          "Network Error",
          "Unable to connect to the server. Please check your internet connection."
        );
      }
      return Promise.reject(error);
    }

    // For property portal, don't show error notifications for 401 (testing mode)
    // BUT allow auth endpoints to show errors
    if (isPropertyPortal && error.response?.status === 401 && !isAuthEndpoint) {
      // Silently fail for property portal - allow testing without auth
      return Promise.reject(error);
    }

    if (
      error.response?.status === 400 ||
      error.response?.status === 404 ||
      error.response?.status === 417
    ) {
      const errorMessage =
        error.response?.data?.message ??
        error.response?.data?.error?.message ??
        error.response?.data?.error ??
        error.response?.data?.detail ??
        "An error occurred";
      notify.error(errorMessage);
      return Promise.reject(error);
    }
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      getRefreshToken()
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return axiosConfig(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await refreshToken();
        const newAccessToken = response.access;
        setLocalAccessToken(newAccessToken);
        if (response.refresh) {
          setLocalRefreshToken(response.refresh);
        }
        axiosConfig.defaults.headers.common["Authorization"] =
          `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return axiosConfig(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearLocalStorage();
        window.location.reload();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    } else {
      // Don't show error notifications for auth endpoints - let the calling code handle it
      if (!isAuthEndpoint) {
        const errorMessage =
          error.response?.data?.error?.message ??
          error.response?.data?.message ??
          error.response?.data?.error ??
          error.response?.data?.detail ??
          error?.message ??
          "An error occurred";
        notify.error(errorMessage);
      }
      return Promise.reject(error);
    }
  }
);

const refreshToken = async () => {
  const response = await axios.post(
    `${import.meta.env.VITE_API_BASE_URL}/auth/token-refresh/`,
    new URLSearchParams({
      refresh: getRefreshToken() ?? "",
    }),
    {
      headers: {
        "X-Tenant-Id": getTenantId(),
        "Content-Type": "application/x-www-form-urlencoded",
      },
    }
  );
  return response.data;
};
